import { startOfDay, subMonths } from "date-fns";
import { twJoin } from "tailwind-merge";

import { Button } from "@/design-system/Button";
import { SQL_SERVER_MIN_DATE } from "@/helpers/date";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useOnScroll } from "@/hooks/useOnScroll";
import { useTranslation } from "@/translations";
import type { PickerRef } from "@/ui-adapters/AntDatePicker";

import { AntDatePicker } from "../DatePicker/picker";
import { connectedPickerPlacements } from "../DatePicker/placements";
import { useConnectedPickerPlacement } from "../DatePicker/useConnectedPickerPlacement";
import { ArrowRightIcon } from "../Icons/ArrowRightIcon";
import { CalendarIcon } from "../Icons/CalendarIcon";

export type DateRangePickerValue = [start: Date, end: Date];

export interface DatePickerProps extends React.RefAttributes<PickerRef> {
  id?: string;
  name?: string;
  placeholder?: [string, string];
  value: DateRangePickerValue | undefined;
  defaultValue?: DateRangePickerValue;
  onChange: (value: DateRangePickerValue | undefined) => void;
  min?: Date;
  max?: Date;
  disabledDate?: (current: Date) => boolean;
  showNow?: boolean;
  error?: boolean;
  "data-testid"?: string;
  ref?: React.Ref<PickerRef>;
}

export function DateRangePicker({
  value,
  defaultValue,
  onChange,
  min,
  max,
  disabledDate,
  showNow = true,
  error,
  ref,
  ...props
}: DatePickerProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const [isOpened, openHandler] = useBool(false);
  const connectedPickerPlacement = useConnectedPickerPlacement(isOpened);

  const { t } = useTranslation();

  const format = "DD/MM/YYYY";

  useOnScroll(() => {
    openHandler.setFalse();
  });

  const handleChange = (dates: [Date | null, Date | null]) => {
    const converted = convertValue(dates);
    if (converted && (isDateDisabled(converted[0]) || isDateDisabled(converted[1]))) {
      return;
    }
    onChange(converted);
  };

  const isDateDisabled = (date: Date) => {
    const effectiveMin = min ?? SQL_SERVER_MIN_DATE;
    if (date < startOfDay(effectiveMin)) {
      return true;
    }

    if (max && date >= max) {
      return true;
    }

    if (disabledDate) {
      return disabledDate(date);
    }

    return false;
  };

  const handleSelectPredefinedRange = (months: number) => {
    // oxlint-disable-next-line react/purity -- This date is read only when the user clicks.
    const endDate = startOfDay(new Date());
    const startDate = startOfDay(subMonths(endDate, months));

    handleChange([startDate, endDate]);
  };

  const todayDate = clockDate;
  const shouldShowNowButton = showNow && !isDateDisabled(todayDate);

  return (
    <AntDatePicker.RangePicker
      {...props}
      className={twJoin("min-w-xs", connectedPickerPlacement.inputClassName)}
      ref={ref}
      open={isOpened}
      onOpenChange={openHandler.set}
      builtinPlacements={connectedPickerPlacements}
      classNames={{ popup: { root: connectedPickerPlacement.popupClassName } }}
      picker="date"
      onCalendarChange={(date) => handleChange(date)}
      value={value}
      format={{
        format,
        type: "mask",
      }}
      disabledDate={isDateDisabled}
      defaultValue={defaultValue}
      minDate={min}
      maxDate={max}
      status={error ? "error" : undefined}
      panelRender={(originalPanel) => (
        <section className="flex">
          {/* Predefined ranges */}
          <section className="flex min-w-control-narrow flex-col gap-space-xs p-space-sm">
            <Button
              variant="subtle"
              size="sm"
              data-testid="date-range-picker-option-last-3-months"
              onClick={() => handleSelectPredefinedRange(3)}
              disabled={isDateDisabled(subMonths(todayDate, 3))}
            >
              {t("common.date-range.last-months", { count: 3 })}
            </Button>
            <Button
              variant="subtle"
              size="sm"
              data-testid="date-range-picker-option-last-6-months"
              onClick={() => handleSelectPredefinedRange(6)}
              disabled={isDateDisabled(subMonths(todayDate, 6))}
            >
              {t("common.date-range.last-months", { count: 6 })}
            </Button>
            <Button
              variant="subtle"
              size="sm"
              data-testid="date-range-picker-option-last-12-months"
              onClick={() => handleSelectPredefinedRange(12)}
              disabled={isDateDisabled(subMonths(todayDate, 12))}
            >
              {t("common.date-range.last-months", { count: 12 })}
            </Button>
          </section>
          {originalPanel}
        </section>
      )}
      renderExtraFooter={
        shouldShowNowButton
          ? () => (
              <footer className="flex justify-end py-space-sm">
                <Button
                  variant="ghostPrimary"
                  size="sm"
                  data-testid="date-picker-now-button"
                  onClick={() => {
                    const now = new Date();
                    handleChange([now, now]);
                  }}
                >
                  {t("component.date-picker.btn.now")}
                </Button>
              </footer>
            )
          : undefined
      }
      size="large"
      separator={<ArrowRightIcon size={16} tone="secondary" />}
      suffixIcon={<CalendarIcon size={16} tone="secondary" />}
    />
  );
}

function convertValue(value: [Date | null, Date | null] | null): DateRangePickerValue | undefined {
  if (!value) {
    return undefined;
  }

  const [start, end] = value;
  if (!start || !end) {
    return undefined;
  }

  return [start, end];
}
